import type { BoardData, Channel, Connection, Item, Port } from '../model/schema.js';
import { itemName, lanePorts, outputsForInput, resolvePatch } from '../trace/ports.js';
import { nodeLabel } from '../trace/summary.js';
import { traceForward } from '../trace/trace.js';
import { fieldColumns } from './columns.js';
import { portLabel } from './helpers.js';

export interface PatchListRow {
  rowKey: string;
  stageboxId: string;
  stagebox: string;
  index: number;
  port: string;
  direction: Port['direction'];
  signal: string;
  connector: string;
  phantom: boolean;
  /** Other end(s) of the cables on this port. */
  connectedTo: string;
  cable: string;
  /** Inputs/trunks: channels whose signal passes this port; outputs: mixes whose monitors it feeds. */
  assignment: string;
}

function otherEnds(
  data: BoardData,
  item: Item,
  port: Port,
): { labels: string[]; cables: string[] } {
  const touching = Object.values(data.connections).filter(
    (c) =>
      (c.from.itemId === item.id && c.from.portId === port.id) ||
      (c.to.itemId === item.id && c.to.portId === port.id),
  );
  const other = (c: Connection): Connection['from'] =>
    c.from.itemId === item.id && c.from.portId === port.id ? c.to : c.from;
  return {
    labels: touching.map((c) => portLabel(data, other(c).itemId, other(c).portId)),
    cables: touching.map((c) =>
      [c.cableType, c.length === undefined ? undefined : `${c.length} m`]
        .filter((s) => s !== undefined)
        .join(' '),
    ),
  };
}

const portKey = (itemId: string, portId: string): string => JSON.stringify([itemId, portId]);

/** Physical start of a channel's signal: its formal source, else a resolvable patch port. */
function channelStart(
  data: BoardData,
  ch: Channel,
): { itemId: string; portId?: string } | undefined {
  if (ch.sourceItemId !== undefined && data.items[ch.sourceItemId] !== undefined) {
    return ch.sourcePortId === undefined
      ? { itemId: ch.sourceItemId }
      : { itemId: ch.sourceItemId, portId: ch.sourcePortId };
  }
  return ch.patch === undefined ? undefined : resolvePatch(data, ch.patch);
}

/**
 * Sub-core lane of a channel (U44): the input `subPatch` names and the output it comes out of,
 * so both ends of the sub-core list the channel even before any cable is drawn.
 */
export function subPatchPorts(
  data: BoardData,
  ch: Channel,
): { itemId: string; portIds: string[] } | undefined {
  if (ch.subPatch === undefined) return undefined;
  const at = resolvePatch(data, ch.subPatch);
  if (at === undefined) return undefined;
  const lanes = lanePorts(data.items[at.itemId]!);
  const k = lanes.inputs.findIndex((p) => p.id === at.portId);
  const outs =
    lanes.trunks.length > 0 ? [] : outputsForInput(lanes.inputs.length, lanes.outputs.length, k);
  return { itemId: at.itemId, portIds: [at.portId, ...outs.map((j) => lanes.outputs[j]!.id)] };
}

/**
 * For every channel, the ports its signal passes through (downstream of its source), plus its
 * informal patch points: the stage-box input of `patch` and both ends of its sub-core lane.
 */
export function channelPortIndex(data: BoardData): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();
  for (const ch of Object.values(data.channels)) {
    const ports = new Set<string>();
    const start = channelStart(data, ch);
    if (start !== undefined) {
      if (start.portId !== undefined) ports.add(portKey(start.itemId, start.portId));
      for (const hop of traceForward(data, start)) {
        for (const node of [hop.from, hop.to]) {
          if (node.type === 'port') ports.add(portKey(node.itemId, node.portId));
        }
      }
    }
    const patched = ch.patch === undefined ? undefined : resolvePatch(data, ch.patch);
    if (patched) ports.add(portKey(patched.itemId, patched.portId));
    const sub = subPatchPorts(data, ch);
    if (sub) for (const id of sub.portIds) ports.add(portKey(sub.itemId, id));
    if (ports.size > 0) index.set(ch.id, ports);
  }
  return index;
}

function assignment(
  data: BoardData,
  index: Map<string, Set<string>>,
  item: Item,
  port: Port,
): string {
  // sub-core outputs carry channels too (U44), other outputs feed mixes
  if (port.direction === 'out' && item.kind !== 'subcore') {
    const reached = new Set(
      traceForward(data, { itemId: item.id, portId: port.id }).flatMap((h) =>
        h.to.type === 'port' || h.to.type === 'item' ? [h.to.itemId] : [],
      ),
    );
    return Object.values(data.mixes)
      .filter((m) => m.outputItemIds.some((id) => reached.has(id)))
      .sort((a, b) => a.number - b.number)
      .map((m) => nodeLabel(data, { type: 'mix', mixId: m.id }))
      .join(', ');
  }
  const key = portKey(item.id, port.id);
  // both sides of a sub-core (U44): the stage box names the lane it comes through, the sub-core
  // output names the stage-box input it goes to
  const side = (ch: Channel): string => {
    if (item.kind === 'stagebox' && ch.subPatch) return ` (via ${ch.subPatch})`;
    if (item.kind === 'subcore' && port.direction === 'out' && ch.patch) return ` → ${ch.patch}`;
    return '';
  };
  return Object.values(data.channels)
    .filter((ch) => index.get(ch.id)?.has(key) === true)
    .sort((a, b) => a.number - b.number)
    .map((ch) => nodeLabel(data, { type: 'channel', channelId: ch.id }) + side(ch))
    .join(', ');
}

/**
 * Patch list of all stageboxes and sub-cores (or of one item): one row per audio port, in port
 * order, with the cable on it and the channels / mixes routed through it.
 */
export function patchList(data: BoardData, stageboxItemId?: string): PatchListRow[] {
  const boxes =
    stageboxItemId === undefined
      ? Object.values(data.items)
          .filter((i) => i.kind === 'stagebox' || i.kind === 'subcore')
          .sort((a, b) => itemName(a).localeCompare(itemName(b)))
      : [data.items[stageboxItemId]].filter((i) => i !== undefined);
  const channelPorts = channelPortIndex(data);
  return boxes.flatMap((item) =>
    (item.ports ?? [])
      .filter((p) => p.signal !== 'power')
      .map((port, index): PatchListRow => {
        const ends = otherEnds(data, item, port);
        return {
          rowKey: `${item.id}/${port.id}`,
          stageboxId: item.id,
          stagebox: itemName(item),
          index: index + 1,
          port: port.name,
          direction: port.direction,
          signal: port.signal,
          connector: port.connector ?? '',
          phantom: port.phantom === true,
          connectedTo: ends.labels.join(', '),
          cable: ends.cables.filter((s) => s !== '').join(', '),
          assignment: assignment(data, channelPorts, item, port),
        };
      }),
  );
}

export const PATCH_LIST_COLUMNS = fieldColumns<PatchListRow>('patch', [
  'stagebox',
  'port',
  'direction',
  'connectedTo',
  'assignment',
  'cable',
  'phantom',
]);
